import {
  QuartrCheckResult,
  QuartrCompanyFields,
  QuartrMatchInfo,
  ReportQuartrIssueRequest,
} from '../../types/contentCompletion';

export const formatQuartrMatchLabel = (match?: QuartrMatchInfo | null): string => {
  if (!match) return 'Unknown Quartr entity';
  const name = match.name || 'Unknown';
  const parts = [name];
  if (match.id) parts.push(`ID ${match.id}`);
  if (match.tickers?.length) parts.push(match.tickers.join(', '));
  return parts.join(' · ');
};

export const formatFieldValue = (
  field: keyof QuartrCompanyFields,
  value?: string | null,
  match?: QuartrMatchInfo | null
): string => {
  if (!value) return '—';
  if (field === 'quartr_company_id' && match) {
    const name = match.name ? ` — ${match.name}` : '';
    const tickers = match.tickers?.length ? ` (${match.tickers.join(', ')})` : '';
    return `${value}${name}${tickers}`;
  }
  return value;
};

export const getCheckResultSummary = (result: QuartrCheckResult): string => {
  if (result.summary) return result.summary;
  if (result.error) return result.error;
  if (result.quartr_match) return formatQuartrMatchLabel(result.quartr_match);
  if (result.warnings[0]) return result.warnings[0];
  return 'Quartr check complete';
};

export const getProposedMatch = (result: QuartrCheckResult): QuartrMatchInfo | undefined =>
  result.proposed_quartr_match || result.quartr_match;

export const getClosestMatchLabel = (result: QuartrCheckResult): string | undefined => {
  const candidate = result.lookup_candidates?.[0];
  return candidate ? formatQuartrMatchLabel(candidate) : undefined;
};

export const QUARTR_REPORTABLE_STATUSES = new Set([
  'lookup_failed',
  'invalid_id',
  'ticker_mismatch',
  'missing_ticker',
]);

export const shouldReportQuartrIssue = (result: QuartrCheckResult): boolean =>
  result.status === 'failure' || QUARTR_REPORTABLE_STATUSES.has(result.check_status);

export const buildRejectedMatchReportRequest = (
  companyId: string,
  companyName: string,
  checkResult: QuartrCheckResult,
  company?: { ticker?: string; quartr_company_id?: string }
): ReportQuartrIssueRequest => {
  const proposedMatch = getProposedMatch(checkResult);
  const proposedLabel = proposedMatch ? formatQuartrMatchLabel(proposedMatch) : undefined;
  return {
    company_id: companyId,
    company_name: companyName,
    issue_type: 'rejected_match',
    ticker: checkResult.current.ticker || company?.ticker,
    quartr_company_id: checkResult.current.quartr_company_id || company?.quartr_company_id,
    summary: proposedLabel
      ? `User rejected proposed Quartr match: ${proposedLabel}`
      : 'User rejected proposed Quartr match',
    closest_match: proposedLabel || getClosestMatchLabel(checkResult),
    searched_queries: checkResult.searched_queries || [],
    note: checkResult.warnings?.[0],
  };
};
